import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Play, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { teamMembers } from '../data/mockData';
import VideoModal from './VideoModal';

const TeamCard = ({ member, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  
  // Mouse position relative to the card for cursor follower
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 150 };
  const cursorX = useSpring(x, springConfig);
  const cursorY = useSpring(y, springConfig);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);
  };

  // Helper to extract YouTube ID and get thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return "https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=800&q=80";
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const id = (match && match[2].length === 11) ? match[2] : null;
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : "";
  };

  const thumbnailUrl = getYouTubeThumbnail(member.videoUrl);

  return (
    <div className="flex-shrink-0 w-[85vw] md:w-[600px] lg:w-[800px] snap-center">
      {/* Video / Image Container */}
      <motion.div
        ref={ref}
        className="relative aspect-video rounded-3xl overflow-hidden cursor-none group bg-gray-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseMove={handleMouseMove}
        onClick={() => onSelect(member)}
        whileTap={{ scale: 0.99 }}
      >
        <img
          src={thumbnailUrl}
          alt={member.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300" />

        {/* Overlay Text: Role on the left */}
        <div className="absolute top-1/2 left-8 md:left-12 -translate-y-1/2 max-w-sm z-10 pointer-events-none">
          <h3 className="text-4xl md:text-5xl lg:text-6xl text-white leading-tight font-medium" 
              style={{ fontFamily: "'Instrument Serif', serif" }}>
            {member.role}
          </h3>
        </div>

        {/* Bottom Right Play Icon (Static) */}
        <div className="absolute bottom-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white pointer-events-none">
           <Play size={20} fill="currentColor" />
        </div>

        {/* Custom Cursor Follower */}
        <motion.div
          className="absolute top-0 left-0 w-28 h-28 bg-white text-black rounded-full flex items-center justify-center gap-2 pointer-events-none z-20 mix-blend-screen"
          style={{
            x: cursorX,
            y: cursorY,
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0,
            translateX: "-50%",
            translateY: "-50%"
          }}
        >
          <span className="font-medium text-xs uppercase tracking-wide">Watch Intro</span>
        </motion.div>
      </motion.div>

      {/* Content Below */}
      <div className="mt-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4 px-2">
        <div className="max-w-xl">
            <h4 className="text-lg font-medium text-[#1a1a1a] mb-2">{member.role}</h4>
            <p className="text-[#5f6368] text-sm leading-relaxed mb-4">{member.bio}</p>
            <button 
                onClick={() => onSelect(member)}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#1a1a1a] hover:gap-2 transition-all"
            >
                View case <ArrowRight size={14} />
            </button>
        </div>
        
        {/* Navigation Arrows for this specific slide context? 
            Actually, commonly these are global for the carousel, 
            but the design puts them inline or below. 
            We'll render them in the parent to control the scroll container. 
        */}
      </div>
    </div>
  );
};

const TeamCarousel = () => {
  const carouselRef = useRef(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const scrollLeft = () => {
    if (carouselRef.current) {
        carouselRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
        carouselRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      {/* Scroll Controls (Top Right or Bottom Right - positioning per design request usually implies proximity) */}
      <div className="absolute -bottom-16 right-0 md:top-[calc(100%+1.5rem)] md:right-10 flex gap-3 z-10">
         <button 
            onClick={scrollLeft}
            className="w-10 h-10 rounded-full border border-[#dadce0] flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
         >
            <ChevronLeft size={18} className="text-[#1a1a1a]" />
         </button>
         <button 
            onClick={scrollRight}
            className="w-10 h-10 rounded-full border border-[#dadce0] flex items-center justify-center hover:bg-[#f8f9fa] transition-colors"
         >
            <ChevronRight size={18} className="text-[#1a1a1a]" />
         </button>
      </div>

      <div 
        ref={carouselRef}
        className="flex gap-8 overflow-x-auto pb-12 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {teamMembers.map((member) => (
          <TeamCard 
            key={member.id} 
            member={member} 
            onSelect={(m) => setSelectedVideo(m.videoUrl)} 
          />
        ))}
      </div>

      <VideoModal
        isOpen={!!selectedVideo}
        videoUrl={selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </div>
  );
};

export default TeamCarousel;
